import { motion } from 'framer-motion'
import React, { useMemo } from 'react'
import { FieldValues, useFormContext } from 'react-hook-form'
import { Button } from '@demandlocal/components'
import { useAtom } from 'jotai'
import { createCampaignFormValuesAtom } from './CreateNewCampaignForm'
import { ReactComponent as FacebookIcon } from '../../../images/icons/facebook_with_cloud.svg'
import { ReactComponent as GoogleIcon } from '../../../images/icons/google_with_cloud.svg'
import { ReactComponent as ISEMIcon } from '../../../images/icons/chart_with_cloud.svg'
import { ReactComponent as DynamicDisplayIcon } from '../../../images/icons/target_with_cloud.svg'
import ProductCardCheckBoxInput from '../../../components/FormElements/ProductCardCheckBoxInput'
import CampaignExampleCard from '../../../components/Campaigns/CampaignExampleCard'
import { upperCase } from 'lodash'
import moment from 'moment'
import toast from 'react-hot-toast'
import { useCreateLinkCampaign } from '../../../api/dealership/campaigns/useCreateLinkCampaign'
import CampaignProgressBar from '../../../components/CampaignProgressBars/CampaignProgressBar'
import classNames from 'classnames'

type Props = {
  step: number
  setStep: (step: number) => void
}

const facebookCards = [
  <CampaignExampleCard
    key={'example1'}
    url="images/campaign/campaign_example3.png"
    title="Example 1"
    textSize="text-xs"
    body="Explanation of this type of banner"
  />,
  <CampaignExampleCard
    key={'example2'}
    url="images/campaign/campaign_example3.png"
    title="Example 2"
    textSize="text-xs"
    body="Explanation of this type of banner"
  />,
  <CampaignExampleCard
    key={'example3'}
    url="images/campaign/campaign_example3.png"
    title="Example 3"
    textSize="text-xs"
    body="Explanation of this type of banner"
  />,
]

const dydCards = [
  <CampaignExampleCard
    key={'example1'}
    url="images/campaign/campaign_example2.png"
    title="Example 1"
    textSize="text-xs"
    body="Explanation of this type of banner"
  />,
  <CampaignExampleCard
    key={'example2'}
    url="images/campaign/campaign_example2.png"
    title="Example 2"
    textSize="text-xs"
    body="Explanation of this type of banner"
  />,
  <CampaignExampleCard
    key={'example3'}
    url="images/campaign/campaign_example2.png"
    title="Example 3"
    textSize="text-xs"
    body="Explanation of this type of banner"
  />,
]

const googleCards = [
  <CampaignExampleCard
    key={'example1'}
    url="images/campaign/campaign_example1.png"
    title="Example 1"
    textSize="text-xs"
    body="Explanation of this type of banner"
  />,
  <CampaignExampleCard
    key={'example2'}
    url="images/campaign/campaign_example1.png"
    title="Example 2"
    textSize="text-xs"
    body="Explanation of this type of banner"
  />,
  <CampaignExampleCard
    key={'example3'}
    url="images/campaign/campaign_example1.png"
    title="Example 3"
    textSize="text-xs"
    body="Explanation of this type of banner"
  />,
]

const CreateNewCampaignProducts = ({ step, setStep }: Props): JSX.Element => {
  const { handleSubmit } = useFormContext()
  const [containerHeight, setContainerHeight] = React.useState(0)
  const [formValues, setFormValues] = useAtom(createCampaignFormValuesAtom)
  const { mutateAsync: createLinkCampaign, isLoading } = useCreateLinkCampaign()

  const products = useMemo(() => {
    return {
      vla: {
        product_code: 'vla',
        product_name: 'Google Vehicle Ads',
        product_description: `Spotlight your most relevant inventory in both text AND image ads with Google's automotive specific shopping ads! Powered by Google's ability to match user generated search queries against your most relevant, up to date inventory. The beauty of Google's Vehicle Ads is the ability to display ALL of your vehicles that match the search query in one ad, meaning multiple Google Vehicle listings in one search results page. These ads are in addition to any existing Google Search campaigns, meaning they can run simultaneously with SEM ads already running, allowing multiple ad placements from multiple angles on a single Search Results Query`,
        icon: <GoogleIcon />,
      },
      dyn: {
        product_code: 'dyn',
        product_name: 'Facebook Dynamic',
        product_description: `Generate high-quality leads and drive them directly to the VDPs of the cars they are interested in buying by using Facebook's in-market auto buyer audience segments, or, by targeting people who have visited or shown interest in your vehicle detail pages (VDPs).`,
        icon: <FacebookIcon />,
      },
      inv: {
        product_code: 'inv',
        product_name: 'Inventory Search Marketing',
        product_description: `Based on daily inventory feeds, create custom paid search campaigns for each vehicle in the dealer's inventory that day. When a shopper searches model specific terms that match a vehicle in the dealer's inventory, a custom SEM ad featuring that vehicle's year, make and model details populates. Clicks drive the consumer to the dealer’s VDP or SRP of the vehicle they were searching for. A custom ad group is dynamically generated for each vehicle in the inventory feed and is updated daily.`,
        icon: <ISEMIcon />,
      },
      dyd: {
        product_code: 'dyd',
        product_name: 'Dynamic Inventory Display',
        product_description: `Serve dynamic inventory based display banners in front of local, lower funnel, in-market autoshoppers. Target customer based on their online shopping behaviors, actual dealership site visits, and your first party customer database. Only serve the images of the inventory the dealer has in stock.`,
        icon: <DynamicDisplayIcon />,
      },
    }
  }, [])

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const pacing_id = moment().unix()

    if (!values.product) {
      toast.error('Please choose a product')
      return
    }

    const response = await createLinkCampaign({
      data: {
        pacing_id: pacing_id,
        product_code: upperCase(values.product),
        objective: formValues?.objective,
        is_incomplete: 1,
      },
    })

    setFormValues((prevFormValues) => {
      return {
        ...prevFormValues,
        pacing_id: pacing_id,
        campaign_id: response.link_campaign_id,
        product: {
          ...products[values.product as keyof typeof products],
        },
      }
    })

    setStep(step + 1)
  }

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className={'flex flex-1 flex-col overflow-auto pb-20'}>
      <motion.div
        ref={(_ref) => {
          if (containerHeight === 0 && _ref?.clientHeight)
            setContainerHeight(_ref?.clientHeight ?? 0)
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={classNames('flex flex-1 flex-col pt-8 pb-8 overflow-auto', {
          'max-h-[auto]': containerHeight === 0,
          'max-h-full]': containerHeight !== 0,
        })}>
        {containerHeight > 0 && (
          <div className={'flex flex-col gap-5 px-12'}>
            <ProductCardCheckBoxInput
              icon={products.dyn.icon}
              name={'product'}
              value={products.dyn.product_code}
              label={products.dyn.product_name}
              description={products.dyn.product_description}
              cards={facebookCards}
              background={'white'}
              type={'radio'}
            />
            <ProductCardCheckBoxInput
              icon={products.vla.icon}
              name={'product'}
              value={products.vla.product_code}
              label={products.vla.product_name}
              description={products.vla.product_description}
              background={'white'}
              cards={googleCards}
              type={'radio'}
            />
            <ProductCardCheckBoxInput
              icon={products.inv.icon}
              name={'product'}
              value={products.inv.product_code}
              label={products.inv.product_name}
              description={products.inv.product_description}
              background={'white'}
              cards={googleCards}
              type={'radio'}
            />
            <ProductCardCheckBoxInput
              icon={products.dyd.icon}
              name={'product'}
              value={products.dyd.product_code}
              label={products.dyd.product_name}
              description={products.dyd.product_description}
              background={'white'}
              cards={dydCards}
              type={'radio'}
            />
          </div>
        )}
      </motion.div>
      <motion.div
        layout
        layoutId={'buttons-container'}
        className={
          'h-20 items-center flex justify-between border-t-2 py-4 px-12 absolute bottom-0 w-full'
        }>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            setStep(step - 1)
          }}>
          Back
        </Button>
        <CampaignProgressBar step={step} />
        <Button variant={'primary'} disabled={isLoading}>
          Continue
        </Button>
      </motion.div>
    </motion.form>
  )
}

export default CreateNewCampaignProducts
